// Adapted from dragndrop/src/components/FileUpload.tsx
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload } from 'lucide-react';
import { DocumentPreview } from './DocumentPreview';
import { Box, CircularProgress, Typography, Button } from '@mui/material';

/**
 * @typedef {import('./types').DocumentMetadata} DocumentMetadata
 */

/**
 * @typedef {object} FileUploadProps
 * @property {(files: File[]) => Promise<void>} onFileUpload
 * @property {DocumentMetadata[]} processingFiles
 * @property {() => void} setNeedsPolling
 */

/**
 * @param {FileUploadProps} props
 * @returns {JSX.Element}
 */
export const FileUpload = ({ onFileUpload, processingFiles, setNeedsPolling, uploadLoading }) => {
  const [isDragging, setIsDragging] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    setIsDragging(false); // Ensure dragging state is reset
    await onFileUpload(acceptedFiles);
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    noClick: true, // Disable opening file dialog on dropzone click, as we have a button
    // Add accepted file types based on your requirements
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      // Add other types as needed
    },
  });

  const dropzoneBaseStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    p: 4,
    borderWidth: '2px',
    borderRadius: '8px', // rounded-lg
    borderColor: 'grey.400', // border-zinc-300
    borderStyle: 'dashed',
    backgroundColor: 'common.white', // Changed background to white
    color: 'text.primary', // Changed text color to primary (black)
    outline: 'none',
    transition: 'border .24s ease-in-out',
    cursor: 'pointer',
    textAlign: 'center',
    minHeight: '150px', // Added min height
    mb: 3, // Added margin bottom
  };

  const dropzoneActiveStyle = {
    borderColor: 'primary.main', // border-blue-500
    backgroundColor: 'action.hover', // Use subtle hover color for active drop
  };

  const dropzoneDraggingStyle = {
    borderColor: 'primary.light', // A slightly different color for dragging
    backgroundColor: 'grey.100', // Light grey for dragging over
  };

  const getDropzoneStyle = () => {
    let style = { ...dropzoneBaseStyle };
    if (isDragActive || isDragging) {
        style = { ...style, ...dropzoneActiveStyle };
    }
    if (isDragging && !isDragActive) { // Handle dragging over but not yet active drop
        style = { ...style, ...dropzoneDraggingStyle };
    }
    return style;
  };

  return (
    <Box sx={{ width: '100%' }}> {/* Ensure outer box takes full width */}
      <Box {...getRootProps()} sx={getDropzoneStyle()}>
        <input {...getInputProps()} />
        <Upload className="h-10 w-10 text-zinc-400 mb-3" />
        {isDragActive ? (
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>Drop the files here ...</Typography>
        ) : (
          <>
            <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1 }}>
              Drag & Drop Files Here
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              or click the button below
            </Typography>
            {uploadLoading && <Box><CircularProgress size={24} /></Box>}
            <Button
              variant="contained"
              onClick={open} // Call the open function from useDropzone
              startIcon={<Upload size={18}/>}
              disabled={uploadLoading}
            >
              SELECT FILES
            </Button>
          </>
        )}
      </Box>

      {/* Display files currently being processed */}
      {processingFiles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'medium' }}>
            Uploading Files
          </Typography>
          <Box sx={{ maxHeight: '200px', overflowY: 'auto', pr: 1 /* Add padding for scrollbar */ }}>
            {processingFiles.map((doc) => (
              <DocumentPreview key={doc.id} document={doc} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
